import { ChangeEvent } from 'react'
import styled from 'styled-components'

import typeStyle from 'styles/typeStyle'

import ChevronSelector from '../../../../components/icons/ChevronSelector'
import useVisitorOrigin from '../../../useVisitorOrigin'
import { CURRENT_LOCATION, appliedOriginCity } from '../../../originCity'

const VISITOR_AREA = 'area'

const PickerLabel = styled.label`
  ${typeStyle('-ds-font-body-regular')}
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-01);
  color: ${({ theme }) => theme['-ds-text-tertiary']};
`

// An inline typographic select: the sentence keeps flowing, only the value
// is interactive. A hidden sizer sharing the grid cell keeps the width on
// the selected label (a bare select sizes to its widest option), and the
// icon overlays the select's end padding so the whole control — chevron
// included — opens the native picker
const OriginSelect = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 240px;
  color: ${({ theme }) => theme['-ds-text-brand']};

  /* Absolute so the widest option can't set the width — the sizer does */
  select {
    position: absolute;
    inset: 0;
    width: 100%;
    appearance: none;
    margin: 0;
    padding: 0 20px 0 0;
    border: 0;
    background: transparent;
    font: inherit;
    color: inherit;
    text-overflow: ellipsis;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 2px;
    }

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }

  svg {
    position: absolute;
    right: 0;
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme['-ds-icon-brand']};
    pointer-events: none;
  }
`

const Sizer = styled.span`
  visibility: hidden;
  padding-right: 20px;
  white-space: nowrap;
`

type Origin = {
  slug: string
  name: string
}

type OriginPickerProps = {
  /** Origins the searched scope can be measured from, from search defaults */
  origins: Origin[]
  /** A city slug, or CURRENT_LOCATION for an explicit current-location pick */
  originCity?: string | null
  /** The server-resolved default origin when it is a city (scope city) */
  defaultOriginCitySlug?: string | null
  /** The visitor's own area can usefully filter the searched scope */
  visitorOriginUsable?: boolean
  share: {
    shareLocation: () => void
    sharing: boolean
    error: string | null
  }
  onChange: (originCity: string | null) => any
}

const OriginPicker = ({
  origins,
  originCity,
  defaultOriginCitySlug,
  visitorOriginUsable,
  share: { shareLocation, sharing },
  onChange,
}: OriginPickerProps) => {
  const origin = useVisitorOrigin()

  const sharedLocation = origin?.source === 'explicit'
  // The resolved city (when there is one) IS the measuring origin, even for
  // a visitor who shared a location the scope can't be reached from
  const choseCurrentLocation =
    originCity === CURRENT_LOCATION &&
    (sharing || (sharedLocation && visitorOriginUsable))
  const selectedCity = appliedOriginCity(originCity)
  const value =
    (choseCurrentLocation && CURRENT_LOCATION) ||
    (origins.some(({ slug }) => slug === selectedCity) && selectedCity) ||
    defaultOriginCitySlug ||
    (sharedLocation && CURRENT_LOCATION) ||
    VISITOR_AREA

  const selectedLabel =
    (value === VISITOR_AREA && 'your area') ||
    (value === CURRENT_LOCATION && 'Current location') ||
    origins.find(({ slug }) => slug === value)?.name ||
    ''

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value
    if (next === CURRENT_LOCATION) {
      shareLocation()
      onChange(CURRENT_LOCATION)
    } else if (next === VISITOR_AREA) {
      onChange(null)
    } else {
      onChange(next)
    }
  }

  return (
    <>
      <PickerLabel>
        Maximum travel time from
        <OriginSelect>
          <select
            aria-label="Drive time origin"
            value={value}
            disabled={sharing}
            onChange={handleChange}
          >
            {/* A shared location is the visitor's area — one origin, one label */}
            {!sharedLocation &&
              (visitorOriginUsable || value === VISITOR_AREA) && (
                <option value={VISITOR_AREA} disabled={!visitorOriginUsable}>
                  your area
                </option>
              )}
            <option value={CURRENT_LOCATION}>Current location</option>
            {origins.map(({ slug, name }) => (
              <option key={slug} value={slug}>
                {name}
              </option>
            ))}
          </select>
          <Sizer aria-hidden>{selectedLabel}</Sizer>
          <ChevronSelector />
        </OriginSelect>
      </PickerLabel>
    </>
  )
}

export default OriginPicker
